import React, { useState, useRef } from "react";
import {
    Grid,
    TextField,
    Button,
    Typography,
    Modal,
    CircularProgress,
    Alert,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "react-i18next";
import { colors } from "./Theme/WebTheme";
import { FirebaseConfig } from "config/FirebaseConfig";

const useStyles = makeStyles((theme) => ({
    paper: {
        width: "90%",
        maxWidth: 500,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[5],
        padding: theme.spacing(3),
        borderRadius: 8,
    },
    modal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    formField: {
        marginBottom: theme.spacing(2),
    },
    submitButton: {
        marginTop: theme.spacing(2),
        backgroundColor: colors.MAIN_COLOR,
        color: colors.WHITE,
        "&:hover": {
            backgroundColor: colors.SECONDARY,
        },
    },
    cancelButton: {
        marginTop: theme.spacing(2),
        marginRight: theme.spacing(1),
        backgroundColor: colors.SECONDARY,
        color: colors.WHITE,
    },
    webview: {
        width: "100%",
        height: "500px",
        border: "none",
        borderRadius: 8,
    },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: 200,
    },
    inputRtl: {
        "& label": {
            right: 20,
            left: "auto",
            paddingRight: 20,
        },
        "& legend": {
            textAlign: "right",
            marginRight: 20,
        },
    },
}));

const PowerTranzCardForm = ({
    open,
    onClose,
    amount,
    orderId,
    custId,
    profile,
    onPaymentComplete,
    onPaymentError,
}) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === "rtl";
    const [loading, setLoading] = useState(false);
    const [showWebview, setShowWebview] = useState(false);
    const [redirectData, setRedirectData] = useState(null);
    const [error, setError] = useState(null);
    const webviewRef = useRef(null);

    // Form state
    const [formData, setFormData] = useState({
        cardPan: "",
        cardCvv: "",
        cardExp: "",
        cardholderName: "",
        firstName: profile?.firstName || "",
        lastName: profile?.lastName || "",
        line1: "",
        city: "",
        state: "",
        postalCode: "",
        countryCode: "840", // Default USD
        emailAddress: profile?.email || "",
        phoneNumber: profile?.mobile || "",
    });

    const handleInputChange = (field) => (event) => {
        let value = event.target.value;

        // Format card number
        if (field === "cardPan") {
            value = value.replace(/\D/g, ""); // Remove non-digits
            value = value.replace(/(.{4})/g, "$1 "); // Add spaces every 4 digits
            value = value.trim();
            if (value.length > 19) value = value.substring(0, 19); // Max 19 chars (16 digits + 3 spaces)
        }

        // Format expiry date
        if (field === "cardExp") {
            value = value.replace(/\D/g, ""); // Remove non-digits
            if (value.length >= 2) {
                value = value.substring(0, 2) + "/" + value.substring(2, 4);
            }
            if (value.length > 5) value = value.substring(0, 5);
        }

        // Format CVV
        if (field === "cardCvv") {
            value = value.replace(/\D/g, ""); // Remove non-digits
            if (value.length > 4) value = value.substring(0, 4);
        }

        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const validateForm = () => {
        const errors = [];

        if (
            !formData.cardPan ||
            formData.cardPan.replace(/\s/g, "").length < 13
        ) {
            errors.push(t("card_number_invalid"));
        }

        if (!formData.cardCvv || formData.cardCvv.length < 3) {
            errors.push(t("cvv_invalid"));
        }

        if (!formData.cardExp || !/^\d{2}\/\d{2}$/.test(formData.cardExp)) {
            errors.push(t("expiry_invalid"));
        }

        if (!formData.cardholderName.trim()) {
            errors.push(t("cardholder_name_required"));
        }

        if (!formData.line1.trim()) {
            errors.push(t("address_required"));
        }

        if (!formData.city.trim()) {
            errors.push(t("city_required"));
        }

        if (
            !formData.emailAddress.trim() ||
            !formData.emailAddress.includes("@")
        ) {
            errors.push(t("email_invalid"));
        }

        return errors;
    };

    const handleSubmit = async () => {
        const validationErrors = validateForm();
        if (validationErrors.length > 0) {
            setError(validationErrors.join(", "));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const serverUrl = `https://us-central1-${FirebaseConfig.projectId}.cloudfunctions.net`;
            const response = await fetch(`${serverUrl}/powertranz-link`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    order_id: orderId,
                    amount: amount,
                    currency: profile.currency_code || "USD",
                    cust_id: custId,
                    ...formData,
                }),
            });

            const result = await response.json();

            if (result.success) {
                // Direct payment success (no 3DS required)
                onPaymentComplete(result);
                onClose();
            } else if (result.requires_3ds && result.redirect_data) {
                // 3DS authentication required
                setRedirectData(result.redirect_data);
                setShowWebview(true);
                setupWebviewMessageListener();
            } else {
                // Payment failed
                setError(result.error || t("payment_failed"));
            }
        } catch (error) {
            console.error("PowerTranz payment error:", error);
            setError(t("payment_processing_error"));
        } finally {
            setLoading(false);
        }
    };

    const setupWebviewMessageListener = () => {
        const handleMessage = (event) => {
            console.log("Received message from webview:", event.data);

            if (event.data.type === "payment_success") {
                // Payment completed successfully
                onPaymentComplete({
                    success: true,
                    transaction_id: event.data.transaction_id,
                    message: "Payment completed successfully",
                });
                cleanup();
            } else if (
                event.data.type === "payment_failed" ||
                event.data.type === "auth_failed"
            ) {
                // Payment failed
                onPaymentError({
                    success: false,
                    error: event.data.message || "Payment failed",
                    transaction_id: event.data.transaction_id,
                });
                cleanup();
            } else if (event.data.type === "payment_error") {
                // Payment error
                onPaymentError({
                    success: false,
                    error: "Payment processing error",
                    transaction_id: event.data.transaction_id,
                });
                cleanup();
            }
        };

        const cleanup = () => {
            window.removeEventListener("message", handleMessage);
            setShowWebview(false);
            setRedirectData(null);
            onClose();
        };

        window.addEventListener("message", handleMessage);

        // Cleanup on unmount or timeout
        setTimeout(() => {
            cleanup();
        }, 300000); // 5 minutes timeout
    };

    const handleClose = () => {
        setFormData({
            cardPan: "",
            cardCvv: "",
            cardExp: "",
            cardholderName: "",
            firstName: profile?.firstName || "",
            lastName: profile?.lastName || "",
            line1: "",
            city: "",
            state: "",
            postalCode: "",
            countryCode: "840",
            emailAddress: profile?.email || "",
            phoneNumber: profile?.mobile || "",
        });
        setError(null);
        setShowWebview(false);
        setRedirectData(null);
        onClose();
    };

    if (showWebview && redirectData) {
        return (
            <Modal open={open} onClose={handleClose} className={classes.modal}>
                <div
                    className={classes.paper}
                    style={{ width: "90%", maxWidth: 800, height: "80%" }}
                >
                    <Typography variant="h6" gutterBottom>
                        {t("secure_authentication")}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        gutterBottom
                    >
                        {t("complete_3ds_authentication")}
                    </Typography>

                    <iframe
                        ref={webviewRef}
                        className={classes.webview}
                        srcDoc={redirectData}
                        title="3DS Authentication"
                        sandbox="allow-scripts allow-forms allow-same-origin allow-top-navigation"
                    />

                    <div style={{ marginTop: 16, textAlign: "center" }}>
                        <Button
                            onClick={handleClose}
                            className={classes.cancelButton}
                        >
                            {t("cancel")}
                        </Button>
                    </div>
                </div>
            </Modal>
        );
    }

    return (
        <Modal open={open} onClose={handleClose} className={classes.modal}>
            <div className={classes.paper}>
                <Typography variant="h6" gutterBottom>
                    {t("powertranz_payment")}
                </Typography>

                {error && (
                    <Alert severity="error" style={{ marginBottom: 16 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <div className={classes.loadingContainer}>
                        <CircularProgress />
                    </div>
                ) : (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSubmit();
                        }}
                    >
                        <Grid container spacing={2}>
                            {/* Card Information */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" gutterBottom>
                                    {t("card_information")}
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t("card_number")}
                                    value={formData.cardPan}
                                    onChange={handleInputChange("cardPan")}
                                    placeholder="1234 5678 9012 3456"
                                    className={isRTL ? classes.inputRtl : ""}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={t("expiry_date")}
                                    value={formData.cardExp}
                                    onChange={handleInputChange("cardExp")}
                                    placeholder="MM/YY"
                                    className={isRTL ? classes.inputRtl : ""}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={t("cvv")}
                                    value={formData.cardCvv}
                                    onChange={handleInputChange("cardCvv")}
                                    placeholder="123"
                                    className={isRTL ? classes.inputRtl : ""}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t("cardholder_name")}
                                    value={formData.cardholderName}
                                    onChange={handleInputChange(
                                        "cardholderName"
                                    )}
                                    className={isRTL ? classes.inputRtl : ""}
                                    required
                                />
                            </Grid>

                            {/* Billing Address */}
                            <Grid item xs={12}>
                                <Typography
                                    variant="subtitle1"
                                    gutterBottom
                                    style={{ marginTop: 16 }}
                                >
                                    {t("billing_address")}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={t("first_name")}
                                    value={formData.firstName}
                                    onChange={handleInputChange("firstName")}
                                    className={isRTL ? classes.inputRtl : ""}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={t("last_name")}
                                    value={formData.lastName}
                                    onChange={handleInputChange("lastName")}
                                    className={isRTL ? classes.inputRtl : ""}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t("address")}
                                    value={formData.line1}
                                    onChange={handleInputChange("line1")}
                                    className={isRTL ? classes.inputRtl : ""}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={t("city")}
                                    value={formData.city}
                                    onChange={handleInputChange("city")}
                                    className={isRTL ? classes.inputRtl : ""}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={t("state")}
                                    value={formData.state}
                                    onChange={handleInputChange("state")}
                                    className={isRTL ? classes.inputRtl : ""}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={t("postal_code")}
                                    value={formData.postalCode}
                                    onChange={handleInputChange("postalCode")}
                                    className={isRTL ? classes.inputRtl : ""}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={t("phone_number")}
                                    value={formData.phoneNumber}
                                    onChange={handleInputChange("phoneNumber")}
                                    className={isRTL ? classes.inputRtl : ""}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t("email")}
                                    type="email"
                                    value={formData.emailAddress}
                                    onChange={handleInputChange("emailAddress")}
                                    className={isRTL ? classes.inputRtl : ""}
                                    required
                                />
                            </Grid>

                            {/* Action Buttons */}
                            <Grid
                                item
                                xs={12}
                                style={{ textAlign: "right", marginTop: 16 }}
                            >
                                <Button
                                    onClick={handleClose}
                                    className={classes.cancelButton}
                                    style={{ marginRight: 8 }}
                                >
                                    {t("cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    className={classes.submitButton}
                                    disabled={loading}
                                >
                                    {t("pay")} {profile.symbol} {amount}
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                )}
            </div>
        </Modal>
    );
};

export default PowerTranzCardForm;
